import {
  creatorLabel,
  creatorsOf,
  FILE_DATE_FILTERS,
  folderTitle,
  typesOf,
  workspacesOf,
  type BrowsableFile,
  type FileCreator,
  type FileFilterState,
} from '../../../lib/file-browser';
import { useAgentName } from '../../../lib/use-agent-name';
import { useTranslation } from '../../../lib/i18n';
import { OverflowMenu, ScrollArea, type OverflowMenuAction } from '../ui';
import { FilesSpaceMark } from './FilesSpaceMark';
import styles from './FilesFilterBar.module.css';

// The Files list's filter row: **Space, then Created by, then Date, then Type** — each a labelled DS picker
// whose label states the current value, plus a Clear that appears once something is narrowed.
//
// It is the row Tasks and Routines already ship, with the two axes only files have added to it. Same chips,
// same reading — a picker's rest label states the whole SET it is offering ("All Spaces", "All agents",
// "Any type") rather than naming the bare dimension, so the row reads as an answer instead of a set of
// prompts. Three lists that ask the same question of the same account should not each phrase it differently.
//
// The Space and Created-by rest labels are the very keys Tasks and Routines use (`filter.allSpaces` /
// `filter.allAgents`, already translated in every bundle); Date and Type keep their own "Any …" wording,
// which says the same thing and is likewise already translated.
//
// **Space leads, and it is the one axis that MOVES you**: picking a Space makes it the browsing root
// (`browseRoot`), which is what lets the cross-workspace screen open flat instead of on a row of workspace
// folders. Created by comes next because it is the question the files themselves are most often narrowed by.
//
// **There is no Owner filter.** It used to be a TAB pair ("Uploaded Files" / "Agent Files"), then briefly a
// fourth picker, and it is gone: created-by supersedes it. Picking yourself under Created by asks the same
// question against the same data, and a row offering both invites the state where the two disagree.
//
// The Space, Type and Created-by option lists are DERIVED FROM THE FILES IN SCOPE (`workspacesOf` / `typesOf` / `creatorsOf`), so a
// menu can never offer a value that matches nothing — a fixed roster of agents goes stale the moment a new
// actor type writes a file. Each control is omitted entirely when there is nothing to choose between.
export function FilesFilterBar({
  items,
  workspaceItems,
  workspaceNames = {},
  filters,
  scopeLabel,
  onChange,
  onBi,
  className,
}: {
  /** The files the options are derived from — everything beneath the folder being browsed. */
  items: BrowsableFile[];
  /** The files the SPACE options are derived from — the whole surface, not the current Space's subtree, or
   *  picking a Space would leave its own menu with one entry and hide the way back out. */
  workspaceItems?: BrowsableFile[];
  /** Workspace id → display name. A Space whose name is unresolved is not offered. */
  workspaceNames?: Record<string, string>;
  filters: FileFilterState;
  /** What the subtree-scope chip is called — the surface's own words ("Riley's files"). Without one the
   *  chip is not drawn, because an unnamed scope is a chip that cannot say what it is doing. */
  scopeLabel?: string;
  onChange: (next: FileFilterState) => void;
  /** Reports which filter was used, for BI. */
  onBi?: (name: string) => void;
  /** Layout from the enclosing row — the bar owns its chips, not its place on the page. */
  className?: string;
}) {
  const { t } = useTranslation();
  const agent = useAgentName();
  const types = typesOf(items);
  const creators = creatorsOf(items);
  const workspaces = workspacesOf(workspaceItems ?? items, workspaceNames);

  const pick = (name: string, next: FileFilterState) => {
    onBi?.(name);
    onChange(next);
  };

  const dateActions: OverflowMenuAction[] = FILE_DATE_FILTERS.map((bucket) => ({
    key: bucket,
    label: t(`files.filter.date.${bucket}`),
    onSelect: () => pick(`filter_date_${bucket}`, { ...filters, date: bucket }),
  }));

  const typeActions: OverflowMenuAction[] = [
    { key: 'any', label: t('files.filter.type.any'), onSelect: () => pick('filter_type_any', { ...filters, type: null }) },
    ...types.map((kind) => ({
      key: kind,
      label: t(`files.type.${kind}`),
      onSelect: () => pick(`filter_type_${kind}`, { ...filters, type: kind }),
    })),
  ];

  const workspaceActions: OverflowMenuAction[] = [
    {
      key: 'any',
      label: t('files.filter.allSpaces'),
      onSelect: () => pick('filter_space_all', { ...filters, workspaceId: null }),
    },
    // Each Space is offered WITH its own badge — the same mark the rows and the chip draw, so the three
    // places a Space appears on this screen are recognisably the same Space. "All spaces" has none:
    // there is no one Space to draw.
    ...workspaces.map((space) => ({
      key: space.id,
      label: space.name,
      icon: <FilesSpaceMark id={space.id} name={space.name} size={20} />,
      onSelect: () => pick('filter_space', { ...filters, workspaceId: space.id }),
    })),
  ];

  const creatorActions: OverflowMenuAction[] = [
    { key: 'any', label: t('files.filter.allAgents'), onSelect: () => pick('filter_creator_any', { ...filters, creator: null }) },
    ...creators.map((creator) => ({
      key: creatorKey(creator),
      label: creatorLabel(creator, t, agent.short),
      onSelect: () => pick('filter_creator', { ...filters, creator }),
    })),
  ];

  // A picker's label carries the current VALUE, which is the whole point of a labelled control over a glyph.
  // At rest it states the whole set ("All Spaces"), the way the Tasks and Routines rows do — a chip reading
  // the bare dimension asks a question where its neighbours give answers.
  const spaceLabel = workspaces.find((space) => space.id === filters.workspaceId)?.name ?? t('files.filter.allSpaces');
  const createdByLabel = filters.creator === null ? t('files.filter.allAgents') : creatorLabel(filters.creator, t, agent.short);
  const dateLabel = filters.date === 'any' ? t('files.filter.date.any') : t(`files.filter.date.${filters.date}`);
  const typeLabel = filters.type === null ? t('files.filter.type.any') : t(`files.type.${filters.type}`);

  // A chip that is AWAY from its rest value grows a ✕ that resets that axis and nothing else (§9). The
  // rest values are the "All …/Any …" ones.
  //
  // ⚠️ THERE IS NO "CLEAR FILTERS" BUTTON, at any width, and its absence is the design's decision rather
  // than an omission. Neither frame draws one — not the desktop header (`15623:362406`), not the phone
  // (`15891:420486`) — because every narrowed chip now carries its own ✕, so the row already says which
  // axes are active and offers the way out of each. THE TRADE: resetting everything is one tap per
  // active chip instead of a single button. That is accepted; a row of chips is at most four, and the
  // per-axis ✕ is the affordance a reader can actually see from the chip they want to undo.
  // `files.filter.clear` survives as that ✕'s accessible NAME — a glyph still has to be announced.
  const clearLabel = t('files.filter.clear');
  // A scope always names itself, whether a component supplied the words or the scope arrived in the URL
  // (`/files?folder=brain/Riley`, from an agent's Files tile). Falling back to the folder's own title
  // matters more than it looks: the chip is the ONLY way to drop the scope, so a scope with no label
  // would be a filter the reader can see the effect of and cannot turn off — the undismissable narrowing
  // this whole row exists to avoid.
  //
  // `folderTitle`, not the raw last segment: it IS the last segment for every folder someone named, and
  // the translated word for the account roots — so `?scope=knowledge` reads "Memory", the same word the
  // folder row and the breadcrumb use, rather than the directory's name in English to every locale.
  const scopeName = scopeLabel ?? folderTitle(filters.folder ?? '', workspaceNames, t);

  return (
    /* ONE row that scrolls sideways rather than wrapping onto two or three, when there is more in it than
       fits. The DS `ScrollArea` owns that overflow; a hand-rolled `overflow-x: auto` is what
       `test/scrollbar-spec.test.ts` exists to stop.

       ⚠️ A CHIP IS SIZED BY ITS LABEL, at every width. Figma's filtered chip (`15891:422056`) carries
       `whitespace-nowrap` on its label, so the chip HUGS — and on a phone the row scrolls sideways
       rather than dividing itself between the chips. Making them equal-width thirds instead collapsed
       every label to a letter and an ellipsis at 375px ("B..", "Las…", "Doc…"), which is a chip that
       has stopped saying what it filters. Reported with a screenshot. */
    <ScrollArea axis="horizontal" bar="hidden" className={className ? `${styles.bar} ${className}` : styles.bar}>
      {/* THE SUBTREE SCOPE, and it leads the row because it is the widest question asked of the list.
          It only appears when a surface applied one (an agent's folder), and it is a chip rather than a
          root precisely so it can be taken off: dismissing it drops the scope, which returns the browser
          to the whole tree in place — same screen, same breadcrumb, now showing everything.

          It carries only a CLEAR, no menu: there is no list of other subtrees to pick from, and a chip
          that opens an empty picker is worse than one that just says what it is and how to remove it. */}
      {filters.folder && (
        <OverflowMenu
          className={styles.chipWrap}
          triggerClassName={styles.chipTrigger}
          label={scopeName}
          ariaLabel={scopeName}
          actions={[]}
          onClear={() => pick('filter_scope_all', { ...filters, folder: null })}
          clearAriaLabel={clearLabel}
        />
      )}
      {workspaces.length > 1 && (
        <OverflowMenu
          className={styles.chipWrap}
          triggerClassName={styles.chipTrigger}
          label={spaceLabel}
          /* The chosen Space wears its badge on the chip too, not only in the menu — at 16 there
             (Figma `15891:422056`) against the menu item's 20, because a chip is a 36px control and the
             menu row is a line of text. */
          labelIcon={filters.workspaceId ? <FilesSpaceMark id={filters.workspaceId} name={spaceLabel} size={16} /> : undefined}
          ariaLabel={t('files.filter.allSpaces')}
          actions={workspaceActions}
          selectedKey={filters.workspaceId ?? 'any'}
          onClear={filters.workspaceId ? () => pick('filter_space_all', { ...filters, workspaceId: null }) : undefined}
          clearAriaLabel={clearLabel}
        />
      )}
      {creators.length > 1 && (
        <OverflowMenu
          className={styles.chipWrap}
          triggerClassName={styles.chipTrigger}
          label={createdByLabel}
          ariaLabel={t('files.createdBy')}
          actions={creatorActions}
          selectedKey={filters.creator ? creatorKey(filters.creator) : 'any'}
          onClear={filters.creator ? () => pick('filter_creator_any', { ...filters, creator: null }) : undefined}
          clearAriaLabel={clearLabel}
        />
      )}
      <OverflowMenu
        className={styles.chipWrap}
        triggerClassName={styles.chipTrigger}
        label={dateLabel}
        ariaLabel={t('agentCreations.table.date')}
        actions={dateActions}
        selectedKey={filters.date}
        onClear={filters.date !== 'any' ? () => pick('filter_date_any', { ...filters, date: 'any' }) : undefined}
        clearAriaLabel={clearLabel}
      />
      {types.length > 1 && (
        <OverflowMenu
          className={styles.chipWrap}
          triggerClassName={styles.chipTrigger}
          label={typeLabel}
          ariaLabel={t('agentCreations.detail.type')}
          actions={typeActions}
          selectedKey={filters.type ?? 'any'}
          onClear={filters.type ? () => pick('filter_type_any', { ...filters, type: null }) : undefined}
          clearAriaLabel={clearLabel}
        />
      )}
    </ScrollArea>
  );
}

/** A creator's menu key. The PAIR identifies a writer (see `FileCreator`), so both halves are in the key —
 *  otherwise two same-typed writers with different names would share one check mark. */
function creatorKey(creator: FileCreator): string {
  return `${creator.type} ${creator.name}`;
}
